import { createContext, useContext } from "react";
import type { Membership, MeResponse, StoreRef } from "../api/types";

export interface ShellContextValue {
  me: MeResponse;
  membership: Membership;
  store: StoreRef;
  orgSlug: string;
  storeSlug: string;
  mocked: boolean;
  openPalette: () => void;
  openStoreSwitcher: () => void;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell(): ShellContextValue {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell must be used inside the app shell");
  return ctx;
}

const LAST_STORE = "ady.lastStore";
export function rememberStore(org: string, store: string) {
  try {
    sessionStorage.setItem(LAST_STORE, `${org}/${store}`);
  } catch {
    // ignore
  }
}
export function lastStore(): { org: string; store: string } | null {
  try {
    const [org, store] = (sessionStorage.getItem(LAST_STORE) ?? "").split("/");
    return org && store ? { org, store } : null;
  } catch {
    return null;
  }
}

/** First org/store the user can open: the last used one if still valid, else the first. */
export function defaultStore(me: MeResponse): { org: string; store: string } | null {
  const last = lastStore();
  if (last) {
    const m = me.memberships.find((x) => x.org.slug === last.org);
    if (m?.stores.some((st) => st.slug === last.store)) return last;
  }
  const first = me.memberships[0];
  const store = first?.stores[0];
  return first && store ? { org: first.org.slug, store: store.slug } : null;
}

export const isMocked = (): boolean =>
  Boolean((globalThis as { __ADY_MOCKS__?: boolean }).__ADY_MOCKS__);
